import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import DualListSelector, { DualListItem } from './common/DualListSelector';
import { ProcedureTemplate } from '../../../services/auditService';
import riskAssessmentService, { AssessmentTemplateRisk } from '../../../services/riskAssessmentService';
import riskRegisterService, { RiskRegister as Risk } from '../../../services/riskRegisterService';
import { extractApiError } from '../../../utils/apiError';

/** Imperative handle exposed so the host modal's footer can drive the save. */
export interface ProcedureConfigHandle {
    save: () => Promise<void>;
}

interface Props {
    procedure: ProcedureTemplate;
    /** Reports save/loading state up so the host footer button can disable itself. */
    onStatusChange?: (status: { saving: boolean; loading: boolean }) => void;
}

// After migration 066 the audit procedure row IS the Risk Assessment template — same
// physical row in ADT_procedure_templates. Configure scope (risks covered), frequency,
// and due-date rule directly via the canonical RSK endpoints. Same data, same backend,
// regardless of whether the user opens it from the Risk module or from Audit Procedures.
const ProcedureRiskAssessment = forwardRef<ProcedureConfigHandle, Props>(({ procedure, onStatusChange }, ref) => {
    const [frequency, setFrequency] = useState<string>('');
    const [dueRule, setDueRule] = useState<string>('');
    const [allRisks, setAllRisks] = useState<Risk[]>([]);
    const [selected, setSelected] = useState<DualListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const initialFrequencyRef = useRef<string>('');
    const initialDueRuleRef = useRef<string>('');

    // Approved templates are frozen: no field edits, no scope changes. Mirror the
    // CAT template config — inputs/scope disabled, save blocked with a toast.
    // Reopen via the status dot to edit.
    const isApproved = procedure.workflow_state === 'approved';

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            riskAssessmentService.get(procedure.id),
            riskRegisterService.getAll(),
            riskAssessmentService.listRisks(procedure.id),
        ]).then(([tpl, risks, scope]) => {
            if (cancelled) return;
            const f = tpl.frequency || '';
            const d = tpl.due_date_program || '';
            setFrequency(f);
            setDueRule(d);
            initialFrequencyRef.current = f;
            initialDueRuleRef.current = d;
            // Only approved risks belong in the scope picker — getAll() returns all
            // workflow states (draft/submitted/rejected leaked in otherwise).
            setAllRisks(risks.filter((r: Risk) => r.workflow_state === 'approved'));
            setSelected(scope.map((r: AssessmentTemplateRisk) => ({
                id: r.id,
                title: r.risk_code,
                description: r.risk_name,
            })));
        }).catch((e: any) => {
            if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load configuration.') });
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [procedure.id]);

    const items: DualListItem[] = allRisks.map(r => ({
        id: r.id,
        title: r.risk_code,
        description: r.risk_name,
        category: r.category_name,
    }));

    const handleSave = useCallback(async () => {
        // Approved templates are frozen — block the save and surface why via a
        // toast, mirroring the CAT template config's approved case.
        if (isApproved) {
            setToast({ type: 'error', message: 'Record is approved and locked. Reopen via the workflow button to edit.' });
            return;
        }
        setSaving(true);
        try {
            if (frequency !== initialFrequencyRef.current || dueRule !== initialDueRuleRef.current) {
                await riskAssessmentService.update(procedure.id, {
                    frequency: frequency || undefined,
                    due_date_program: dueRule || undefined,
                });
                initialFrequencyRef.current = frequency;
                initialDueRuleRef.current = dueRule;
            }
            await riskAssessmentService.replaceScope(procedure.id, selected.map(s => Number(s.id)));
            setToast({ type: 'success', message: 'Configuration saved.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save configuration.') });
        } finally {
            setSaving(false);
        }
    }, [frequency, dueRule, selected, procedure.id, isApproved]);

    // Expose save() to the host modal footer + report status for its disabled state.
    useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);
    useEffect(() => { onStatusChange?.({ saving, loading }); }, [saving, loading, onStatusChange]);

    return (
        <>
            <div className="flex flex-col gap-y-4 pt-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <FormControl.Input
                        label="Frequency"
                        data-testid="INP_risk_assessment_template_frequency"
                        value={frequency}
                        onChange={e => setFrequency(e.target.value)}
                        disabled={isApproved}
                        placeholder="e.g. Annual"
                    />
                    <FormControl.Input
                        label="Due Date Rule"
                        data-testid="INP_risk_assessment_template_due_rule"
                        value={dueRule}
                        onChange={e => setDueRule(e.target.value)}
                        disabled={isApproved}
                        placeholder="e.g. End of fiscal year"
                    />
                </div>

                <p className="text-sm font-semibold text-gray-700">Scope — Risks covered</p>
                <div style={{ height: 340 }}>
                    {loading ? (
                        <div className="h-full flex items-center justify-center text-gray-500">Loading…</div>
                    ) : (
                        <DualListSelector
                            availableItems={items}
                            selectedItems={selected}
                            onSelectionChange={setSelected}
                            disabled={isApproved}
                            titleLeft="Available Risks"
                            titleRight="Risks in Template"
                            searchPlaceholder="Search risks…"
                            emptyMessageLeft="No more risks available"
                            emptyMessageRight="No risks selected"
                        />
                    )}
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
});

ProcedureRiskAssessment.displayName = 'ProcedureRiskAssessment';

export default ProcedureRiskAssessment;
